/**
 * CreateCompanyDialog - Modal para criação de nova empresa
 * Layout 2 colunas com sidebar stepper simplificado
 * Cria empresas automaticamente no plano Free (forçado pelo backend)
 *
 * SIMPLIFICADO: Removida etapa de seleção de plano
 * Upgrade de planos deve ser feito via /tenant/plans após criação
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-18 v2.0.0 - Alinhado ao Onboarding: adicionados os campos
 *            obrigatórios exigidos pela RPC `create_tenant_self_service`
 *            (país, PF/PJ, CPF/CNPJ, CEP/UF/Cidade para BR, ramo de atividade
 *            e principal dor) + mensagens de erro amigáveis.
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSlugValidation, generateSlugFromName } from "@/hooks/useSlugValidation";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { validateCPF, validateCNPJ } from "@/lib/input-validators";
import { countryCodes } from "@/lib/country-codes";
import { getFriendlyOnboardingError } from "@/lib/onboardingErrors";
import { 
  Building2, 
  Link2, 
  Check, 
  X, 
  Loader2,
  Sparkles,
  CheckCircle2,
  AlertCircle,
  ChevronsUpDown,
  Search,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { logger } from "@/lib/logger";

// ============================================================================
// UTILITÁRIOS (mesmas regras do Onboarding)
// ============================================================================

/** Aplica máscara de CPF (000.000.000-00) */
const maskCPF = (v: string) => v.replace(/\D/g, '').slice(0, 11)
  .replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');
/** Aplica máscara de CNPJ (00.000.000/0000-00) */
const maskCNPJ = (v: string) => v.replace(/\D/g, '').slice(0, 14)
  .replace(/^(\d{2})(\d)/, '$1.$2').replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
  .replace(/\.(\d{3})(\d)/, '.$1/$2').replace(/(\d{4})(\d)/, '$1-$2');
/** Aplica máscara de CEP (00000-000) */
const maskCEP = (v: string) => v.replace(/\D/g, '').slice(0, 8).replace(/(\d{5})(\d)/, '$1-$2');

/** Sanitiza input removendo caracteres potencialmente perigosos (anti-XSS) */
const sanitizeInput = (input: string): string =>
  input
    .replace(/[<>]/g, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+=/gi, '')
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '');


// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

interface CreateCompanyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ============================================================================
// CONFIGURAÇÃO DOS STEPS (Simplificado - sem seleção de plano)
// ============================================================================

const STEPS = [
  { id: 1, title: "Dados da Empresa", subtitle: "Nome e identificador", icon: Building2 },
  { id: 2, title: "Concluído", subtitle: "Pronto para usar", icon: CheckCircle2 },
];

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function CreateCompanyDialog({ open, onOpenChange }: CreateCompanyDialogProps) {
  const navigate = useNavigate();
  const { user, refreshTenants, switchTenant } = useAuth();
  
  // Estados do wizard
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  
  // Estados do formulário
  const [companyName, setCompanyName] = useState("");
  const [slug, setSlug] = useState("");

  // [v2.0.0] Perfil da empresa — mesmos campos exigidos pela RPC no onboarding
  const [dialCode, setDialCode] = useState<string>('+55');
  const [dialPopoverOpen, setDialPopoverOpen] = useState(false);
  const [personType, setPersonType] = useState<'pf' | 'pj'>('pf');
  const [taxId, setTaxId] = useState('');
  const [taxIdError, setTaxIdError] = useState<string | null>(null);
  const [cep, setCep] = useState('');
  const [cepError, setCepError] = useState<string | null>(null);
  const [uf, setUf] = useState('');
  const [cidade, setCidade] = useState('');
  const [isLoadingCep, setIsLoadingCep] = useState(false);
  const [industry, setIndustry] = useState('');
  const [mainPain, setMainPain] = useState('');

  // Brasil ativa o fluxo fiscal completo (PF/PJ, CPF/CNPJ, endereço)
  const isBR = dialCode === '+55';
  const selectedCountry = countryCodes.find(c => c.code === dialCode) ?? countryCodes[0];

  // Hook de validação de slug em tempo real
  const { status: slugStatus, message: slugMessage, checkSlug } = useSlugValidation();

  /** Consulta o ViaCEP quando o CEP tiver 8 dígitos e preenche UF/Cidade. */
  const handleCepBlur = useCallback(async () => {
    const clean = cep.replace(/\D/g, '');
    if (clean.length !== 8) {
      setCepError(clean.length > 0 ? 'CEP deve ter 8 dígitos' : null);
      return;
    }
    setCepError(null);
    setIsLoadingCep(true);
    try {
      const resp = await fetch(`https://viacep.com.br/ws/${clean}/json/`);
      const data = await resp.json();
      if (data.erro) {
        setCepError('CEP não encontrado');
        return;
      }
      setUf(String(data.uf || '').toUpperCase());
      setCidade(String(data.localidade || ''));
    } catch {
      setCepError('Erro ao consultar CEP');
    } finally {
      setIsLoadingCep(false);
    }
  }, [cep]);

  /** Aplica máscara conforme PF/PJ e limpa erro anterior. */
  const handleTaxIdChange = useCallback((raw: string) => {
    setTaxId(personType === 'pf' ? maskCPF(raw) : maskCNPJ(raw));
    setTaxIdError(null);
  }, [personType]);

  /** Validação client-side dos campos extras (mesma regra do onboarding). */
  const extraFieldsValid = (() => {
    if (!industry.trim() || industry.trim().length > 50) return false;
    if (!mainPain.trim() || mainPain.trim().length > 200) return false;
    if (!isBR) return true;
    const digits = taxId.replace(/\D/g, '');
    if (personType === 'pf' && digits.length !== 11) return false;
    if (personType === 'pj' && digits.length !== 14) return false;
    if (cep.replace(/\D/g, '').length !== 8) return false;
    if (!uf.trim() || !cidade.trim()) return false;
    return true;
  })();


  // ============================================================================
  // EFEITOS
  // ============================================================================

  // Gera slug a partir do nome da empresa e valida em tempo real
  useEffect(() => {
    if (companyName) {
      const generatedSlug = generateSlugFromName(companyName);
      setSlug(generatedSlug);
      checkSlug(generatedSlug);
    }
  }, [companyName, checkSlug]);

  // ============================================================================
  // FUNÇÕES
  // ============================================================================

  // Handler para alteração manual do slug
  const handleSlugChange = (value: string) => {
    const normalizedSlug = value.toLowerCase().replace(/[^a-z0-9-]/g, "");
    setSlug(normalizedSlug);
    checkSlug(normalizedSlug);
  };

  // Cria empresa diretamente (sem etapa de seleção de plano)
  const handleNext = async () => {
    if (step === 1) {
      // Valida slug antes de criar
      if (slugStatus !== 'available') {
        toast.error("Verifique o identificador antes de continuar");
        return;
      }
      // [v2.0.0] Valida dígitos verificadores de CPF/CNPJ no fluxo BR
      if (isBR) {
        const digits = taxId.replace(/\D/g, '');
        const check = personType === 'pf' ? validateCPF(digits) : validateCNPJ(digits);
        if (!check.isValid) {
          setTaxIdError(check.error || 'Documento inválido');
          toast.error(check.error || 'Documento inválido');
          return;
        }
      }
      // Cria empresa diretamente no plano Free
      await handleCreate();
    }
  };

  // Cria a empresa no plano Free (forçado pelo backend)
  const handleCreate = async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    
    try {
      // Backend sempre força plano Free, independente do parâmetro enviado.
      // [v2.0.0] Payload completo — mesmos campos enviados pelo onboarding.
      const { data, error } = await supabase.rpc("create_tenant_self_service", {
        _user_id: user.id,
        _tenant_name: sanitizeInput(companyName).trim(),
        _tenant_slug: sanitizeInput(slug).trim(),
        _plan: "free", // Sempre Free - backend valida isso
        _country_code: selectedCountry?.iso2 ?? null,
        _person_type: isBR ? personType : null,
        _tax_id: isBR ? taxId.replace(/\D/g, '') : null,
        _cep: isBR ? cep.replace(/\D/g, '') : null,
        _uf: isBR ? uf.trim().toUpperCase() : null,
        _cidade: isBR ? cidade.trim() : null,
        _industry: sanitizeInput(industry).trim().slice(0, 50),
        _main_pain: sanitizeInput(mainPain).trim().slice(0, 200),
      } as any);
      
      if (error) throw error;
      
      const result = data as any;
      
      if (!result.success) {
        if (result.error === "already_has_free_plan") {
          toast.error("Você já possui uma empresa no plano Free. Faça upgrade ou desative a empresa existente.");
          return;
        }
        throw new Error(result.message || "Erro ao criar empresa");
      }
      
      logger.info("CreateCompanyDialog", "Empresa criada com sucesso: " + result.tenant_id);
      
      // Atualiza lista de tenants disponíveis (busca do banco)
      const updatedTenants = await refreshTenants?.();
      
      // Muda para o novo tenant (agora está na lista atualizada)
      if (result.tenant_id && updatedTenants?.length) {
        switchTenant?.(result.tenant_id);
      }
      
      // Avança para step de sucesso
      setStep(2);
      
      toast.success("Empresa criada com sucesso!");
      
      // Redireciona após 2 segundos
      setTimeout(() => {
        onOpenChange(false);
        resetForm();
        navigate("/tenant");
      }, 2000);
      
    } catch (error: any) {
      logger.error("[CreateCompanyDialog] Erro ao criar empresa", error);
      // [v2.0.0] Mensagens amigáveis (mesmo helper do onboarding)
      toast.error(getFriendlyOnboardingError(error?.message));
    } finally {
      setIsLoading(false);
    }
  };

  // Reseta o formulário
  const resetForm = () => {
    setStep(1);
    setCompanyName("");
    setSlug("");
    setDialCode('+55');
    setPersonType('pf');
    setTaxId('');
    setTaxIdError(null);
    setCep('');
    setCepError(null);
    setUf('');
    setCidade('');
    setIndustry('');
    setMainPain('');
  };


  // Fecha o modal
  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  // ============================================================================
  // RENDERIZAÇÃO
  // ============================================================================

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent 
        className="max-w-3xl p-0 gap-0 overflow-hidden"
        preventCloseOnOutsideInteraction={step === 2}
      >
        <div className="flex h-[min(80vh,640px)]">
          {/* ================================================================ */}
          {/* SIDEBAR COM STEPPER */}
          {/* ================================================================ */}
          <aside className="w-72 bg-gradient-to-b from-slate-900 to-slate-800 p-6 flex flex-col text-white shrink-0">
            {/* Logo/Header */}
            <div className="flex items-center gap-3 mb-8">
              <div className="h-10 w-10 rounded-xl bg-primary/20 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">Nova Empresa</h3>
                <p className="text-xs text-slate-400">Configure sua organização</p>
              </div>
            </div>

            {/* Stepper Vertical */}
            <nav className="flex-1">
              <ul className="space-y-1">
                {STEPS.map((s, index) => {
                  const isCompleted = step > s.id;
                  const isActive = step === s.id;
                  const Icon = s.icon;
                  
                  return (
                    <li key={s.id} className="relative">
                      {/* Linha conectora */}
                      {index < STEPS.length - 1 && (
                        <div 
                          className={cn(
                            "absolute left-5 top-14 w-0.5 h-6 transition-colors duration-300",
                            isCompleted ? "bg-primary" : "bg-slate-700"
                          )}
                        />
                      )}
                      
                      {/* Step item */}
                      <div 
                        className={cn(
                          "flex items-center gap-4 p-3 rounded-xl transition-all duration-300",
                          isActive && "bg-white/10",
                          !isActive && !isCompleted && "opacity-50"
                        )}
                      >
                        {/* Círculo do step */}
                        <div 
                          className={cn(
                            "h-10 w-10 rounded-full flex items-center justify-center transition-all duration-300 shrink-0",
                            isCompleted && "bg-primary text-primary-foreground",
                            isActive && "bg-primary/20 text-primary ring-2 ring-primary/30",
                            !isActive && !isCompleted && "bg-slate-700 text-slate-400"
                          )}
                        >
                          {isCompleted ? (
                            <Check className="h-5 w-5" />
                          ) : (
                            <Icon className="h-5 w-5" />
                          )}
                        </div>
                        
                        {/* Texto do step */}
                        <div>
                          <p className={cn(
                            "text-sm font-medium transition-colors",
                            isActive ? "text-white" : "text-slate-400"
                          )}>
                            {s.title}
                          </p>
                          <p className="text-xs text-slate-500">
                            {s.subtitle}
                          </p>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* Resumo em tempo real */}
            {(companyName || slug) && step === 1 && (
              <div className="pt-4 border-t border-slate-700/50 animate-fade-in">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">Resumo</p>
                {companyName && (
                  <div className="flex items-center gap-2 text-sm mb-2">
                    <Building2 className="h-4 w-4 text-slate-500" />
                    <span className="text-slate-300 truncate">{companyName}</span>
                  </div>
                )}
                {slug && (
                  <div className="flex items-center gap-2 text-sm mb-2">
                    <Link2 className="h-4 w-4 text-slate-500" />
                    <span className="text-slate-400 text-xs truncate">/{slug}</span>
                  </div>
                )}
                {/* Mostra plano Free fixo */}
                <div className="flex items-center gap-2 text-sm">
                  <Sparkles className="h-4 w-4 text-emerald-500" />
                  <span className="text-emerald-400 text-xs">Plano Free</span>
                </div>
              </div>
            )}
          </aside>

          {/* ================================================================ */}
          {/* CONTEÚDO PRINCIPAL */}
          {/* ================================================================ */}
          <main className="flex-1 flex flex-col bg-background">
            {/* Conteúdo do step */}
            <div className="flex-1 p-8 overflow-y-auto">
              {/* Step 1: Dados da Empresa */}
              {step === 1 && (
                <div className="animate-fade-in space-y-6">
                  <div>
                    <h2 className="text-2xl font-semibold text-foreground">
                      Dados da Empresa
                    </h2>
                    <p className="text-muted-foreground mt-1">
                      Informe os dados básicos da sua nova organização
                    </p>
                  </div>

                  <div className="space-y-5">
                    {/* Campo Nome */}
                    <div className="space-y-2">
                      <Label htmlFor="company-name" className="text-sm font-medium">
                        Nome da Empresa
                      </Label>
                      <div className="relative">
                        <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                        <Input
                          id="company-name"
                          value={companyName}
                          onChange={(e) => setCompanyName(e.target.value)}
                          placeholder="Minha Empresa LTDA"
                          className="h-12 pl-12 rounded-xl text-base"
                        />
                      </div>
                    </div>

                    {/* Campo Slug/URL */}
                    <div className="space-y-2">
                      <Label htmlFor="company-slug" className="text-sm font-medium">
                        Identificador URL
                      </Label>
                      <div className="flex items-center gap-0">
                        <div className="h-12 px-4 bg-muted rounded-l-xl border border-r-0 border-input flex items-center">
                          <span className="text-sm text-muted-foreground whitespace-nowrap">
                            app.uaizychat.com.br/
                          </span>
                        </div>
                        <div className="relative flex-1">
                          <Input
                            id="company-slug"
                            value={slug}
                            onChange={(e) => handleSlugChange(e.target.value)}
                            placeholder="minha-empresa"
                            className={cn(
                              "h-12 rounded-l-none rounded-r-xl text-base pr-12",
                              slugStatus === 'unavailable' && "border-destructive focus-visible:ring-destructive",
                              slugStatus === 'available' && "border-emerald-500 focus-visible:ring-emerald-500"
                            )}
                          />
                          {/* Indicador de status inline */}
                          <div className="absolute right-4 top-1/2 -translate-y-1/2">
                            {slugStatus === 'checking' && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
                            {slugStatus === 'unavailable' && <X className="h-5 w-5 text-destructive" />}
                            {slugStatus === 'available' && <Check className="h-5 w-5 text-emerald-500" />}
                          </div>
                        </div>
                      </div>
                      
                      {/* Mensagem de status abaixo do campo */}
                      {slug && slugStatus === 'checking' && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1.5 animate-fade-in">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Verificando disponibilidade...
                        </p>
                      )}
                      {slugStatus === 'unavailable' && (
                        <p className="text-sm text-destructive flex items-center gap-1.5 animate-fade-in">
                          <AlertCircle className="h-4 w-4" />
                          {slugMessage || 'Este identificador não está disponível'}
                        </p>
                      )}
                      {slugStatus === 'available' && (
                        <p className="text-sm text-emerald-600 flex items-center gap-1.5 animate-fade-in">
                          <CheckCircle2 className="h-4 w-4" />
                          Disponível!
                        </p>
                      )}
                      {slug && slug.length < 3 && slugStatus === 'idle' && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                          <AlertCircle className="h-4 w-4" />
                          Mínimo de 3 caracteres
                        </p>
                      )}
                    </div>

                    {/* ============================================== */}
                    {/* [v2.0.0] Perfil da empresa (exigido pela RPC)   */}
                    {/* ============================================== */}

                    {/* País / DDI */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">DDI / País</Label>
                      <Popover open={dialPopoverOpen} onOpenChange={setDialPopoverOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            role="combobox"
                            aria-expanded={dialPopoverOpen}
                            className="w-full h-12 rounded-xl justify-between font-normal"
                          >
                            <span className="flex items-center gap-2 truncate">
                              <span className="text-lg leading-none">{selectedCountry.flag}</span>
                              <span className="font-mono font-medium">{selectedCountry.code}</span>
                              <span className="text-muted-foreground truncate">— {selectedCountry.country}</span>
                            </span>
                            <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="p-0 w-[--radix-popover-trigger-width] max-w-none" align="start">
                          <Command
                            filter={(value, search) => {
                              const q = search.toLowerCase().replace(/\s+/g, '');
                              return value.toLowerCase().includes(q) ? 1 : 0;
                            }}
                          >
                            <CommandInput placeholder="Buscar país ou DDI (ex.: brasil, +55)" className="h-11" />
                            <CommandList className="max-h-64">
                              <CommandEmpty>Nenhum país encontrado.</CommandEmpty>
                              <CommandGroup>
                                {countryCodes.map((c, idx) => {
                                  const key = `${c.iso2 ?? 'xx'}-${c.code}-${idx}`;
                                  const value = `${c.country} ${c.code} ${c.iso2 ?? ''}`;
                                  const isSelected = c.code === dialCode && c.iso2 === selectedCountry.iso2;
                                  return (
                                    <CommandItem
                                      key={key}
                                      value={value}
                                      onSelect={() => { setDialCode(c.code); setDialPopoverOpen(false); }}
                                      className="flex items-center gap-3"
                                    >
                                      <span className="text-lg leading-none">{c.flag}</span>
                                      <span className="flex-1 truncate">{c.country}</span>
                                      <span className="font-mono text-sm text-muted-foreground">{c.code}</span>
                                      {isSelected && <Check className="h-4 w-4 text-primary" />}
                                    </CommandItem>
                                  );
                                })}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Campos exclusivos do fluxo Brasil */}
                    {isBR && (
                      <>
                        {/* Tipo de pessoa */}
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Tipo de Cadastro</Label>
                          <RadioGroup
                            value={personType}
                            onValueChange={(v) => {
                              setPersonType(v as 'pf' | 'pj');
                              setTaxId('');
                              setTaxIdError(null);
                            }}
                            className="grid grid-cols-2 gap-3"
                          >
                            <label className={cn(
                              "flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors",
                              personType === 'pf' ? "border-primary bg-primary/5" : "border-border/50"
                            )}>
                              <RadioGroupItem value="pf" />
                              <span className="text-sm font-medium">Pessoa Física</span>
                            </label>
                            <label className={cn(
                              "flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors",
                              personType === 'pj' ? "border-primary bg-primary/5" : "border-border/50"
                            )}>
                              <RadioGroupItem value="pj" />
                              <span className="text-sm font-medium">Pessoa Jurídica</span>
                            </label>
                          </RadioGroup>
                        </div>

                        {/* CPF / CNPJ */}
                        <div className="space-y-2">
                          <Label htmlFor="company-tax-id" className="text-sm font-medium">
                            {personType === 'pf' ? 'CPF' : 'CNPJ'}
                          </Label>
                          <Input
                            id="company-tax-id"
                            inputMode="numeric"
                            placeholder={personType === 'pf' ? '000.000.000-00' : '00.000.000/0000-00'}
                            value={taxId}
                            onChange={(e) => handleTaxIdChange(e.target.value)}
                            className="h-12 rounded-xl text-base font-mono"
                          />
                          {taxIdError && (
                            <p className="text-sm text-destructive flex items-center gap-1.5">
                              <X className="h-4 w-4" /> {taxIdError}
                            </p>
                          )}
                        </div>

                        {/* CEP + UF + Cidade */}
                        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_2fr] gap-3">
                          <div className="space-y-2">
                            <Label htmlFor="company-cep" className="text-sm font-medium">CEP</Label>
                            <div className="relative">
                              <Input
                                id="company-cep"
                                inputMode="numeric"
                                placeholder="00000-000"
                                value={cep}
                                onChange={(e) => { setCep(maskCEP(e.target.value)); setCepError(null); }}
                                onBlur={handleCepBlur}
                                className="h-12 rounded-xl text-base font-mono pr-10"
                              />
                              {isLoadingCep ? (
                                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                              ) : (
                                <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                              )}
                            </div>
                            {cepError && <p className="text-xs text-destructive">{cepError}</p>}
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="company-uf" className="text-sm font-medium">UF</Label>
                            <Input
                              id="company-uf"
                              maxLength={2}
                              value={uf}
                              onChange={(e) => setUf(e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))}
                              className="h-12 w-20 rounded-xl text-base text-center font-mono uppercase"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="company-cidade" className="text-sm font-medium">Cidade</Label>
                            <Input
                              id="company-cidade"
                              value={cidade}
                              onChange={(e) => setCidade(sanitizeInput(e.target.value))}
                              placeholder="Sua cidade"
                              className="h-12 rounded-xl text-base"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {/* Ramo de atividade */}
                    <div className="space-y-2">
                      <Label htmlFor="company-industry" className="text-sm font-medium">Ramo de atividade</Label>
                      <Input
                        id="company-industry"
                        value={industry}
                        onChange={(e) => setIndustry(sanitizeInput(e.target.value).slice(0, 50))}
                        placeholder="Ex.: Clínica odontológica"
                        className="h-12 rounded-xl text-base"
                      />
                    </div>

                    {/* Principal dor */}
                    <div className="space-y-2">
                      <Label htmlFor="company-main-pain" className="text-sm font-medium">Principal dor</Label>
                      <Textarea
                        id="company-main-pain"
                        value={mainPain}
                        onChange={(e) => setMainPain(sanitizeInput(e.target.value).slice(0, 200))}
                        placeholder="O que você mais precisa resolver hoje?"
                        className="min-h-[80px] rounded-xl text-base resize-none"
                      />
                      <p className="text-xs text-muted-foreground text-right">{mainPain.length}/200</p>
                    </div>
                  </div>

                </div>
              )}

              {/* Step 2: Sucesso */}
              {step === 2 && (
                <div className="animate-fade-in flex flex-col items-center justify-center h-full text-center">
                  {/* Animação de sucesso */}
                  <div className="relative mb-6">
                    <div className="h-20 w-20 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center animate-scale-in">
                      <CheckCircle2 className="h-10 w-10 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    {/* Círculos decorativos */}
                    <div className="absolute inset-0 -m-2 rounded-full border-2 border-emerald-200 dark:border-emerald-800 animate-ping opacity-75" />
                    <div className="absolute inset-0 -m-4 rounded-full border border-emerald-100 dark:border-emerald-900" />
                  </div>

                  <h2 className="text-2xl font-semibold text-foreground mb-2">
                    Empresa Criada!
                  </h2>
                  <p className="text-muted-foreground max-w-sm mb-8">
                    Sua empresa <strong>{companyName}</strong> foi criada com sucesso. 
                    Você será redirecionado automaticamente.
                  </p>

                  {/* Resumo final */}
                  <div className="bg-muted/50 rounded-2xl p-6 w-full max-w-sm space-y-4 mb-6">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-xl bg-background flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-primary" />
                      </div>
                      <div className="text-left">
                        <p className="text-xs text-muted-foreground">Empresa</p>
                        <p className="font-medium text-foreground">{companyName}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-xl bg-background flex items-center justify-center">
                        <Link2 className="h-5 w-5 text-primary" />
                      </div>
                      <div className="text-left">
                        <p className="text-xs text-muted-foreground">URL</p>
                        <p className="font-medium text-foreground">/{slug}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-xl bg-background flex items-center justify-center">
                        <Sparkles className="h-5 w-5 text-emerald-500" />
                      </div>
                      <div className="text-left">
                        <p className="text-xs text-muted-foreground">Plano</p>
                        <p className="font-medium text-foreground">Free</p>
                      </div>
                    </div>
                  </div>

                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>

            {/* ================================================================ */}
            {/* FOOTER COM NAVEGAÇÃO - Apenas no step 1 */}
            {/* ================================================================ */}
            {step === 1 && (
              <footer className="p-6 border-t bg-muted/30 flex items-center justify-between shrink-0">
                <Button
                  variant="ghost"
                  onClick={handleClose}
                  className="gap-2"
                >
                  Cancelar
                </Button>

                <Button
                  onClick={handleNext}
                  disabled={
                    isLoading || 
                    !companyName || 
                    !slug || 
                    slug.length < 3 ||
                    slugStatus !== 'available' ||
                    !extraFieldsValid

                  }
                  className="gap-2 min-w-[140px]"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Criando...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      Criar Empresa
                    </>
                  )}
                </Button>
              </footer>
            )}
          </main>
        </div>
      </DialogContent>
    </Dialog>
  );
}
